import { Component, input, effect, signal, untracked, ChangeDetectionStrategy } from '@angular/core';
import { FormGroup, FormControl, Validators, FormArray, ValidatorFn, AsyncValidatorFn } from '@angular/forms';

@Component({
    selector: 'app-input',
    template: '',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class InputComponent {
    readonly model = input<any>();
    readonly meta = input<any>();
    readonly formGroup = input<FormGroup<any>>();
    readonly formArray = input<FormArray<any>>();
    readonly arrayIndex = input<number>();
    readonly readonly = input<boolean>();

    readonly formControl = signal<FormControl<any> | undefined>(undefined);

    protected get formError(): string {
        return this.formControl()?.errors?.form;
    }

    constructor() {
        effect((onCleanup) => {
            const meta = this.meta();
            const formGroup = this.formGroup();
            const formArray = this.formArray();

            const formControl = new FormControl<any>('', {
                validators: this.getValidators(),
                asyncValidators: this.getAsyncValidators(),
                updateOn: meta?.updateOn ?? 'change'
            });

            this.formControl.set(formControl);

            let active = true;
            let registered = false;

            queueMicrotask(() => {
                if (!active) {
                    return;
                }

                if (formGroup) {
                    if (meta && !formGroup.contains(meta.id)) {
                        formGroup.addControl(meta.id, formControl);
                        registered = true;
                    }
                } else if (formArray) {
                    formArray.push(formControl);
                    registered = true;
                }
            });

            // Do not remove.
            // Cleanup function to remove the form control when the component is destroyed or the effect is re-run,
            // preventing memory leaks and ensuring proper form state management.
            onCleanup(() => {
                active = false;

                if (!registered) {
                    return;
                }

                if (formGroup?.get(meta.id) === formControl) {
                    formGroup.removeControl(meta.id);
                }

                if (formArray) {
                    const index = formArray.controls.indexOf(formControl);

                    if (index !== -1) {
                        formArray.removeAt(index);
                    }
                }
            });
        });

        effect(() => {
            const formControl = this.formControl();

            if (formControl) {
                this.setDefault();
            }
        });

        effect(() => {
            const readonly = this.readonly();
            const formControl = untracked(() => this.formControl());

            if (formControl) {
                if (readonly) {
                    formControl.disable();
                } else {
                    formControl.enable();
                }
            }
        });
    }

    protected setDefault() {
        this.formControl()?.setValue(this.model());
    }

    protected getValidators(): ValidatorFn[] {
        const validators: ValidatorFn[] = [];
        const meta = this.meta();
        if (meta && meta.mandatory) {
            validators.push(Validators.required);
        }
        return validators;
    }

    protected getAsyncValidators(): AsyncValidatorFn[] {
        const validators: AsyncValidatorFn[] = [];
        return validators;
    }
}
